/**
 * Toolbar action dropdowns for the Meta detail pages — the Meta siblings of Google's
 * ``CampaignActionsDropdown`` (header variant) and the Google ad page's "Ad" dropdown (#6189).
 *
 * Same chrome as Google's: an outline "Campaign" / "Ad" trigger with a chevron sitting beside
 * Go Live, slate menu items, and the red delete item last. Unlike Google's (which own their
 * mutations), these are presentational — the panels already own the Meta mutations and the
 * delete confirm modals, so the dropdowns just take callbacks + pending flags.
 */

import { ChevronDown, Pause, Play, Trash2 } from 'lucide-react';

import { Spinner } from '@/components/glitch-ui/spinner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ITEM_CLASS = 'cursor-pointer px-3 py-2 text-sm hover:bg-slate-50';
const DISABLED_CLASS = 'opacity-50 cursor-not-allowed';

/** The Meta campaign detail toolbar's "Campaign" dropdown: Pause Campaign + Delete Campaign. */
export function MetaCampaignActionsDropdown({
  canPause,
  onPause,
  isPausing,
  onDelete,
  isDeleting,
}: {
  /** Whether Pause Campaign is offered — the campaign is ACTIVE and mutating surfaces are up. */
  canPause: boolean;
  onPause: () => void;
  isPausing: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-9 gap-2 text-gray-900">
          Campaign
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px] shadow-lg rounded-lg border border-slate-100">
        {canPause && (
          <DropdownMenuItem
            onClick={onPause}
            disabled={isPausing}
            className={`${ITEM_CLASS} ${isPausing ? DISABLED_CLASS : ''}`}
          >
            {isPausing ? (
              <Spinner size="sm" className="shrink-0 mr-2" />
            ) : (
              <Pause className="h-4 w-4 shrink-0 mr-2 text-slate-600" />
            )}
            {isPausing ? 'Pausing...' : 'Pause Campaign'}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onDelete}
          disabled={isDeleting}
          className={`${ITEM_CLASS} text-red-600 ${isDeleting ? DISABLED_CLASS : ''}`}
        >
          {isDeleting ? <Spinner size="sm" className="shrink-0 mr-2" /> : <Trash2 className="h-4 w-4 shrink-0 mr-2" />}
          {isDeleting ? 'Deleting...' : 'Delete Campaign'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The Meta ad detail toolbar's "Ad" dropdown: Pause/Resume Ad + Delete Ad (#6190). */
export function MetaAdActionsDropdown({
  canToggleStatus,
  isAdActive,
  onToggleStatus,
  isToggling,
  onDelete,
  isDeleting,
}: {
  /** Whether Pause/Resume Ad is offered — false until the campaign's first launch (#6191):
   *  Meta creates everything PAUSED, so a never-launched ad would read as resumable when Go
   *  Live is the only real path to delivery (and the launch cascade would overwrite a
   *  pre-launch toggle anyway). Google's analog gate is ``campaign.first_published_at``. */
  canToggleStatus: boolean;
  isAdActive: boolean;
  onToggleStatus: () => void;
  isToggling: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-9 gap-2 text-gray-900">
          Ad
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px] shadow-lg rounded-lg border border-slate-100">
        {canToggleStatus && (
          <DropdownMenuItem
            onClick={onToggleStatus}
            disabled={isToggling}
            className={`${ITEM_CLASS} ${isToggling ? DISABLED_CLASS : ''}`}
          >
            {isToggling ? (
              <Spinner size="sm" className="shrink-0 mr-2" />
            ) : isAdActive ? (
              <Pause className="h-4 w-4 shrink-0 mr-2 text-slate-600" />
            ) : (
              <Play className="h-4 w-4 shrink-0 mr-2 text-slate-600" />
            )}
            {isAdActive ? 'Pause Ad' : 'Resume Ad'}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onDelete}
          disabled={isDeleting}
          className={`${ITEM_CLASS} text-red-600 ${isDeleting ? DISABLED_CLASS : ''}`}
        >
          {isDeleting ? <Spinner size="sm" className="shrink-0 mr-2" /> : <Trash2 className="h-4 w-4 shrink-0 mr-2" />}
          {isDeleting ? 'Deleting...' : 'Delete Ad'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
